/**
 * Process-graph rendering — the authored per-use-case process maps (`GET /console/processes`)
 * drawn as a layered diagram (Admin-Konsole, `/console/processes/:slug`).
 *
 * HAND-ROLLED, and deliberately so. The layout is ALREADY AUTHORED: every node carries a
 * `layer` (band, top→bottom) and a `lane` (position within that band), so there is nothing
 * for an auto-layout engine to decide — pulling in dagre/mermaid/d3 would replace an exact,
 * reviewed arrangement with a heuristic one and add a dependency to do it. Geometry here is
 * therefore a pure function of (layer, lane); the only computed things are the CONNECTORS.
 *
 * Same idiom as `schwartz-wheel.ts`: inline SVG through the `svg` tag, theme-aware purely
 * through the light-DOM `.pg-*` tokens in `index.html`, no dependency of its own. The one
 * departure: the NODES are HTML `<button>`s positioned over the SVG rather than `<g>`s inside
 * it. A node is a genuine affordance — clickable, focusable, keyboard-operable, named for a
 * screen reader — and the delegated `data-act` dispatch (AX-3) requires a real `<button>`/`<a>`.
 * SVG owns the connectors, which are not affordances at all (`aria-hidden`); the edge meaning
 * reaches assistive tech through each node button's accessible name and the disclosure panel.
 *
 * EDGE KINDS render distinctly because they mean different things (see `process-maps.ts`):
 * `flow` is the ordinary path, `repair` is a seam re-asking ITSELF (a genuine self-loop —
 * `planner-seam → planner-seam` — which a naive line renderer draws as a zero-length nothing),
 * and `fallback` is the failure path. A back edge (target above source) is routed out through
 * a right-hand gutter rather than drawn straight through the bands it crosses.
 */

import { html, svg, classMap, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { CareerI18n, CareerKey } from "../../i18n"

// ─── The wire shapes, as this module consumes them ──────────────────────────────
// Mirrors of `contract-v2` `ConsoleProcessNode`/`Edge`/`Map` — app-elements does not depend on
// contract-v2 (see the surface's header note), so the wire record is parsed defensively into
// these by `career-processes.ts` rather than trusted.

export type ProcessNodeKind = "user-step" | "ai-seam" | "event" | "input-asset"
export type ProcessEdgeKind = "flow" | "repair" | "fallback"

export const PROCESS_NODE_KINDS: readonly ProcessNodeKind[] = ["user-step", "ai-seam", "event", "input-asset"]
export const PROCESS_EDGE_KINDS: readonly ProcessEdgeKind[] = ["flow", "repair", "fallback"]

export interface ProcessNodeView {
  readonly id: string
  readonly label: string
  readonly kind: ProcessNodeKind
  readonly note: string
  readonly layer: number
  readonly lane: number
  readonly integrationKey?: string
  readonly promptId?: string
  readonly activityType?: string
  readonly stepLabels?: readonly string[]
  readonly route?: string
  readonly deterministic?: boolean
}

export interface ProcessEdgeView {
  readonly from: string
  readonly to: string
  readonly label: string
  readonly kind: ProcessEdgeKind
}

export interface ProcessMapView {
  readonly slug: string
  readonly title: string
  readonly summary: string
  readonly nodes: readonly ProcessNodeView[]
  readonly edges: readonly ProcessEdgeView[]
}

/** German node-kind vocabulary — what a node IS, in the operator's terms. */
export const NODE_KIND_KEY: Record<ProcessNodeKind, CareerKey> = {
  "user-step": "processes.kind.userStep",
  "ai-seam": "processes.kind.aiSeam",
  event: "processes.kind.event",
  "input-asset": "processes.kind.inputAsset",
}

/** German edge-kind vocabulary — why this connector looks different from its neighbour. */
export const EDGE_KIND_KEY: Record<ProcessEdgeKind, CareerKey> = {
  flow: "processes.edge.flow",
  repair: "processes.edge.repair",
  fallback: "processes.edge.fallback",
}

// ─── Geometry ───────────────────────────────────────────────────────────────────

/** Node box, in px. Fixed — a band is a row of equal boxes, never a measured layout. */
export const NODE_W = 208
export const NODE_H = 66
/** Horizontal gap between two lanes of the same band. */
export const LANE_GAP = 30
/**
 * MINIMUM vertical gap between two bands. The real gap is computed per band from how many
 * connectors leave it (see {@link layoutMap}): a band that emits four labelled edges — one node
 * in the shipped `w1-interview` map does — needs room for four German sentences stacked in the
 * space below it, and a gap tuned to a single label makes all four unreadable.
 */
export const LAYER_GAP = 104
/** Vertical room one (up to two-line) edge label needs. */
export const LABEL_ROW_H = 30
export const PAD = 14
/** How far a self-loop bulges out to the right of its own node. */
export const SELF_R = 30
/** Width of one back-edge return lane in the right-hand gutter. */
export const BACK_LANE = 26
/** Room kept to the right of the graph for back-edge / self-loop labels. */
export const LABEL_ALLOWANCE = 176

export interface NodeBox {
  readonly node: ProcessNodeView
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
  /** The COMPRESSED band index (authored `layer` values may skip numbers). */
  readonly band: number
}

export interface GraphLayout {
  readonly boxes: readonly NodeBox[]
  readonly byId: ReadonlyMap<string, NodeBox>
  readonly width: number
  readonly height: number
  /** Right edge of the widest node — where the back-edge gutter starts. */
  readonly lanesRight: number
  /** Back-edge → its return-lane index in the gutter. */
  readonly backLane: ReadonlyMap<string, number>
  /** Non-back edge → its label rung among the edges leaving the same band. */
  readonly labelRow: ReadonlyMap<string, number>
  /** Band → how many label rungs its gap holds. */
  readonly labelRows: ReadonlyMap<number, number>
  /** Band → the vertical gap BELOW it (px). Sized from {@link labelRows}. */
  readonly gapAfter: readonly number[]
}

/** A stable identity for one edge (from+to alone is not unique — two nodes may be joined twice). */
export const edgeKey = (e: ProcessEdgeView): string => `${e.from}\u0000${e.to}\u0000${e.label}`

const whole = (n: number): number => (Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0)

/**
 * Positions for one authored map. `layer` values are RANK-COMPRESSED (a map that jumps 0,1,3
 * still draws as three tight bands); `lane` is used as authored, so the same lane keeps the
 * same column down the whole graph — which is what makes "this is the happy path, that is the
 * fallback beside it" readable at a glance.
 */
export function layoutMap(map: ProcessMapView): GraphLayout {
  const bands = [...new Set(map.nodes.map((n) => whole(n.layer)))].sort((a, b) => a - b)
  const bandOf = new Map(map.nodes.map((n) => [n.id, Math.max(0, bands.indexOf(whole(n.layer)))]))

  // Pass 1 — route each edge and claim its label rung. This runs BEFORE the boxes are placed
  // because the rungs decide how tall each gap has to be.
  const backLane = new Map<string, number>()
  const labelRow = new Map<string, number>()
  const labelRows = new Map<number, number>()
  let backs = 0
  let selves = 0
  for (const edge of map.edges) {
    const from = bandOf.get(edge.from)
    const to = bandOf.get(edge.to)
    if (from === undefined || to === undefined) continue
    if (edge.from !== edge.to && to < from) {
      // A back edge labels itself out in the gutter, so it claims no rung in the gap.
      backLane.set(edgeKey(edge), backs)
      backs += 1
      continue
    }
    if (edge.from === edge.to) selves += 1
    const row = labelRows.get(from) ?? 0
    labelRow.set(edgeKey(edge), row)
    labelRows.set(from, row + 1)
  }

  // Pass 2 — place the bands, each gap sized to the rungs it must hold.
  const gapAfter = bands.map((_b, i) => Math.max(LAYER_GAP, (labelRows.get(i) ?? 0) * LABEL_ROW_H + 24))
  const bandY: number[] = []
  let y = PAD
  for (let i = 0; i < bands.length; i += 1) {
    bandY.push(y)
    y += NODE_H + (gapAfter[i] ?? LAYER_GAP)
  }

  const boxes: NodeBox[] = map.nodes.map((node) => {
    const band = bandOf.get(node.id) ?? 0
    const lane = whole(node.lane)
    return { node, x: PAD + lane * (NODE_W + LANE_GAP), y: bandY[band] ?? PAD, w: NODE_W, h: NODE_H, band }
  })
  const byId = new Map(boxes.map((b) => [b.node.id, b]))
  const lanesRight = boxes.reduce((m, b) => Math.max(m, b.x + b.w), PAD)

  const gutter = backs > 0 || selves > 0 ? backs * BACK_LANE + LABEL_ALLOWANCE : 0
  return {
    boxes,
    byId,
    lanesRight,
    backLane,
    labelRow,
    labelRows,
    gapAfter,
    width: lanesRight + gutter + PAD,
    height: (bandY.at(-1) ?? PAD) + NODE_H + PAD,
  }
}

/** How a connector is routed — each shape exists because a straight line fails for that case. */
export type EdgeShape = "self" | "back" | "sideways" | "forward"

export interface EdgeGeometry {
  readonly d: string
  readonly labelX: number
  readonly labelY: number
  readonly anchor: "start" | "middle"
  readonly shape: EdgeShape
}

/**
 * The path for one edge. `null` when either endpoint is missing (a map that names a node it
 * does not ship — draw nothing rather than a line into empty space).
 *
 * - `self`     — a seam re-asking ITSELF. Drawn as a bulge out of the node's right flank and
 *                back into it. A `from === to` line has zero length and renders as NOTHING,
 *                which is exactly how a repair loop silently disappears from a graph.
 * - `back`     — the target sits ABOVE the source (a later step handing control back). Routed
 *                out to a right-hand gutter, up, and back in, so it never crosses the bands.
 * - `sideways` — same band, another lane. Dips below the band rather than squeezing a German
 *                sentence into a 30px lane gap.
 * - `forward`  — the ordinary case: a cubic from the source's foot to the target's head.
 */
export function edgeGeometry(layout: GraphLayout, edge: ProcessEdgeView): EdgeGeometry | null {
  const a = layout.byId.get(edge.from)
  const b = layout.byId.get(edge.to)
  if (!a || !b) return null

  // The label rung: every connector LEAVING a band gets its own line in the gap below it, so
  // three or four edges out of one band never stack their German sentences on each other.
  const rungs = layout.labelRows.get(a.band) ?? 1
  const rung = layout.labelRow.get(edgeKey(edge)) ?? 0
  const gap = layout.gapAfter[a.band] ?? LAYER_GAP
  const offset = (gap * (rung + 1)) / (rungs + 1)

  if (edge.from === edge.to) {
    const x = a.x + a.w
    const y1 = a.y + a.h * 0.3
    const y2 = a.y + a.h * 0.78
    return {
      d: `M ${x} ${round(y1)} C ${x + SELF_R} ${round(y1 - 14)}, ${x + SELF_R} ${round(y2 + 14)}, ${x} ${round(y2)}`,
      // The loop bulges out of the RIGHT flank, but its label goes in the gap BELOW the node,
      // on the node's own column: to the right of the flank is where the next LANE's node sits,
      // and a label under a node box is a label nobody can read.
      labelX: round(a.x + a.w / 2),
      labelY: round(a.y + a.h + offset),
      anchor: "middle",
      shape: "self",
    }
  }

  if (b.band < a.band) {
    const lane = layout.backLane.get(edgeKey(edge)) ?? 0
    const gx = layout.lanesRight + 12 + lane * BACK_LANE
    const sy = round(a.y + a.h / 2)
    const ty = round(b.y + b.h / 2)
    return {
      d: `M ${a.x + a.w} ${sy} H ${gx} V ${ty} H ${b.x + b.w}`,
      labelX: gx + 9,
      // Anchored at the SOURCE end, not the midpoint: two back edges whose spans overlap would
      // otherwise stack their labels on top of each other.
      labelY: sy - 9,
      anchor: "start",
      shape: "back",
    }
  }

  if (b.band === a.band) {
    const sx = round(a.x + a.w / 2)
    const tx = round(b.x + b.w / 2)
    const foot = a.y + a.h
    const dip = round(foot + offset + 12)
    return {
      d: `M ${sx} ${foot} C ${sx} ${dip}, ${tx} ${dip}, ${tx} ${b.y + b.h}`,
      labelX: round((sx + tx) / 2),
      labelY: round(foot + offset),
      anchor: "middle",
      shape: "sideways",
    }
  }

  const sx = round(a.x + a.w / 2)
  const tx = round(b.x + b.w / 2)
  const sy = a.y + a.h
  const ty = b.y
  const dy = ty - sy
  return {
    d: `M ${sx} ${sy} C ${sx} ${round(sy + dy * 0.5)}, ${tx} ${round(ty - dy * 0.5)}, ${tx} ${ty}`,
    labelX: round((sx + tx) / 2),
    // Clamped into the run so a band-skipping edge still labels itself between its endpoints.
    labelY: round(Math.min(sy + offset, ty - 14)),
    anchor: "middle",
    shape: "forward",
  }
}

const round = (n: number): number => Math.round(n * 10) / 10

/**
 * Wrap a German transition label into at most `maxLines` short lines. The full text always
 * stays reachable (the caller puts it in a `<title>`), so an elided line loses nothing.
 */
export function wrapLabel(text: string, maxChars = 30, maxLines = 2): string[] {
  const words = text.split(/\s+/).filter((w) => w.length > 0)
  const lines: string[] = []
  let cur = ""
  for (const w of words) {
    const next = cur.length > 0 ? `${cur} ${w}` : w
    if (next.length > maxChars && cur.length > 0) {
      lines.push(cur)
      cur = w
    } else {
      cur = next
    }
  }
  if (cur.length > 0) lines.push(cur)
  if (lines.length <= maxLines) return lines
  const kept = lines.slice(0, maxLines)
  const last = kept[maxLines - 1] ?? ""
  kept[maxLines - 1] = `${last.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`
  return kept
}

// ─── Render ─────────────────────────────────────────────────────────────────────

const LABEL_LINE_H = 11.5
const LABEL_CHAR_W = 5.1

/** One connector's LINE (path + arrowhead). Its title carries the full, unelided German label. */
function edgeLine(layout: GraphLayout, edge: ProcessEdgeView): TemplateResult | typeof nothing {
  const geo = edgeGeometry(layout, edge)
  if (!geo) return nothing
  return svg`
    <g class=${`pg-edge pg-edge--${edge.kind} pg-edge--${geo.shape}`}>
      <title>${edge.label}</title>
      <path class="pg-edge-line" d=${geo.d} marker-end=${`url(#pg-arrow-${edge.kind})`} />
    </g>
  `
}

/**
 * One connector's LABEL, on a legibility plate. Drawn in a SECOND pass, after every line: SVG
 * paints in document order, so a label emitted beside its own path is struck through by the next
 * edge's line — which is how half the German transitions became unreadable the first time.
 */
function edgeLabel(layout: GraphLayout, edge: ProcessEdgeView): TemplateResult | typeof nothing {
  const geo = edgeGeometry(layout, edge)
  if (!geo) return nothing
  const lines = wrapLabel(edge.label)
  const widest = lines.reduce((m, l) => Math.max(m, l.length), 0)
  const plateW = widest * LABEL_CHAR_W + 10
  const plateH = lines.length * LABEL_LINE_H + 5
  const plateX = geo.anchor === "middle" ? geo.labelX - plateW / 2 : geo.labelX - 4
  const firstY = geo.labelY - ((lines.length - 1) * LABEL_LINE_H) / 2
  return svg`
    <g class=${`pg-edge pg-edge--${edge.kind} pg-edge--${geo.shape}`}>
      <title>${edge.label}</title>
      <rect class="pg-edge-plate" x=${round(plateX)} y=${round(geo.labelY - plateH / 2)} width=${round(plateW)} height=${round(plateH)} rx="2" />
      <text class="pg-edge-label" x=${geo.labelX} y=${round(firstY)} text-anchor=${geo.anchor} dominant-baseline="middle" lang="de">
        ${lines.map((line, i) => svg`<tspan x=${geo.labelX} dy=${i === 0 ? 0 : LABEL_LINE_H}>${line}</tspan>`)}
      </text>
    </g>
  `
}

/** One arrowhead per edge kind — a marker cannot inherit its path's stroke, so it gets its own class. */
function arrowDefs(): TemplateResult {
  return svg`
    <defs>
      ${PROCESS_EDGE_KINDS.map(
        (kind) => svg`
          <marker
            id=${`pg-arrow-${kind}`}
            class=${`pg-arrow pg-arrow--${kind}`}
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" />
          </marker>
        `,
      )}
    </defs>
  `
}

/**
 * One node, as an HTML button positioned over the connector layer. The accessible name carries
 * what the shape and colour say visually — the kind, whether it is a deterministic floor, and
 * the label — so the graph is not a picture that only sighted operators can read.
 */
function nodeButton(box: NodeBox, selected: boolean, t: CareerI18n["t"]): TemplateResult {
  const { node } = box
  const kindLabel = t(NODE_KIND_KEY[node.kind])
  const deterministic = node.deterministic === true
  const name = deterministic ? `${node.label} — ${kindLabel}, ${t("processes.deterministic")}` : `${node.label} — ${kindLabel}`
  return html`
    <button
      type="button"
      class=${classMap({
        "pg-node": true,
        [`pg-node--${node.kind}`]: true,
        "is-deterministic": deterministic,
        "is-selected": selected,
      })}
      style=${`left:${box.x}px;top:${box.y}px;width:${box.w}px;height:${box.h}px`}
      data-act="select-node"
      data-node=${node.id}
      aria-pressed=${selected ? "true" : "false"}
      aria-label=${name}
      lang="de"
    >
      <span class="pg-node-kind" aria-hidden="true">${kindLabel}${deterministic ? ` · ${t("processes.deterministic")}` : ""}</span>
      <span class="pg-node-label" aria-hidden="true">${node.label}</span>
    </button>
  `
}

/** The node-kind + edge-kind key, so an operator can read the picture without being told. */
export function processLegend(t: CareerI18n["t"]): TemplateResult {
  return html`
    <ul class="pg-legend" aria-label=${t("processes.legend.title")}>
      ${PROCESS_NODE_KINDS.map(
        (kind) => html`<li class=${`pg-legend-item pg-legend-node pg-node--${kind}`} lang="de">
          <span class="pg-legend-swatch" aria-hidden="true"></span>${t(NODE_KIND_KEY[kind])}
        </li>`,
      )}
      <li class="pg-legend-item pg-legend-node is-deterministic" lang="de">
        <span class="pg-legend-swatch" aria-hidden="true"></span>${t("processes.deterministic")}
      </li>
      ${PROCESS_EDGE_KINDS.map(
        (kind) => html`<li class=${`pg-legend-item pg-legend-edge pg-edge--${kind}`} lang="de">
          <span class="pg-legend-rule" aria-hidden="true"></span>${t(EDGE_KIND_KEY[kind])}
        </li>`,
      )}
    </ul>
  `
}

/**
 * The whole diagram for one map. The SVG scrolls INSIDE `.pg-scroll` (`overflow:auto`) — the
 * canvas is sized in px from the authored layout, so a wide graph never makes the page itself
 * scroll sideways.
 */
export function processGraph(map: ProcessMapView, opts: { selectedId: string | null; t: CareerI18n["t"] }): TemplateResult {
  const { selectedId, t } = opts
  const layout = layoutMap(map)
  return html`
    <div class="pg-scroll" tabindex="0" role="group" aria-label=${t("processes.graph.aria", { title: map.title })}>
      <div class="pg-canvas" style=${`width:${layout.width}px;height:${layout.height}px`}>
        <svg class="pg-edges" viewBox=${`0 0 ${layout.width} ${layout.height}`} width=${layout.width} height=${layout.height} aria-hidden="true">
          ${arrowDefs()}
          <g class="pg-edge-layer">${map.edges.map((e) => edgeLine(layout, e))}</g>
          <g class="pg-label-layer">${map.edges.map((e) => edgeLabel(layout, e))}</g>
        </svg>
        ${layout.boxes.map((b) => nodeButton(b, b.node.id === selectedId, t))}
      </div>
    </div>
  `
}
